import { Alert, Button, Modal, Select, Stack, Textarea } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { errorCode } from '../personnel/source.js';
import work from '../pms/PmsWork.module.css';
import { TimeField } from './DrillRecordFields.js';
import classes from './DrillRecords.module.css';
import type { DrillRecordSource } from './record-source.js';
import type { DrillRecord, DrillRecordDetail, DrillRecords } from './record-types.js';
import { normalizeTime } from './record-view.js';

/** Sunucu hata kodunun metni: önce icra kaydı, sonra plan ve kütüphane, yoksa genel. */
export function useRecordErrorText() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const code = errorCode(error);
    return t(`drillRecord.errors.${code}`, {
      defaultValue: t(`drillPlan.errors.${code}`, {
        defaultValue: t(`drills.errors.${code}`, { defaultValue: t('drillPlan.errors.GENERIC') }),
      }),
    });
  };
}

export function RecordFailure({ error }: { error: unknown }) {
  const errorText = useRecordErrorText();
  if (!error) return null;
  return (
    <p className={work.error} role="alert">
      {errorText(error)}
    </p>
  );
}

/** Ortak çalışma: kaydeder, hatayı gösterir. */
function useAction<T>(run: () => Promise<T | undefined>, onDone: (value: T) => void) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const go = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const value = await run();
      if (value !== undefined) onDone(value);
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };
  return { busy, failure, setFailure, go };
}

/* ---------- Başlatma ---------- */

/**
 * "Conduct Drill" (belge §5): Planner satırından (plan seçili gelir) ya da plansız (şablon).
 * Plansız başlatmada gemide aynı tatbikatın planı varsa kayıt ona bağlanır (sunucu).
 */
export function StartRecordDialog({
  opened,
  data,
  planId,
  source,
  onClose,
  onStarted,
}: {
  opened: boolean;
  data: DrillRecords;
  planId: string | null;
  source: DrillRecordSource;
  onClose: () => void;
  onStarted: (record: DrillRecord) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillRecord.start.${key}`);
  const today = data.vessels[0]?.today ?? '';
  const [choice, setChoice] = useState<string | null>(null);
  const [date, setDate] = useState(today);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    if (!opened) return;
    setChoice(planId ? `plan:${planId}` : null);
    setDate(today);
    setMissing(false);
  }, [opened, planId, today]);
  const plan = choice?.startsWith('plan:')
    ? data.plans.find((p) => p.id === choice.slice(5))
    : undefined;
  const templateId = plan?.templateId ?? (choice?.startsWith('tpl:') ? choice.slice(4) : null);
  const action = useAction(async () => {
    if (!templateId || !date) {
      setMissing(true);
      return undefined;
    }
    return source.start?.({ templateId, planId: plan?.id ?? null, drillDate: date });
  }, onStarted);
  if (!opened) return null;
  const templateName = (id: string) => data.templates.find((x) => x.id === id)?.name ?? '';
  const options = [
    {
      group: k('planned'),
      items: data.plans.map((p) => ({
        value: `plan:${p.id}`,
        label: `${p.templateCode} · ${templateName(p.templateId)}`,
      })),
    },
    {
      group: k('unplanned'),
      items: data.templates
        .filter((x) => !data.plans.some((p) => p.templateId === x.id))
        .map((x) => ({ value: `tpl:${x.id}`, label: `${x.code} · ${x.name}` })),
    },
  ].filter((g) => g.items.length > 0);
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={k('title')}
      closeButtonProps={{ 'aria-label': t('drills.close') }}
    >
      <Stack gap="sm">
        <p className={classes.hint}>{k('hint')}</p>
        <Select
          label={k('drill')}
          required
          searchable
          data={options}
          value={choice}
          onChange={(value) => {
            setChoice(value);
            setMissing(false);
          }}
          error={missing && !templateId ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
        />
        {plan?.openRecordId ? <Alert color="yellow">{k('openExists')}</Alert> : null}
        <DayInput
          label={k('date')}
          description={k('dateHint')}
          required
          maxDate={today || undefined}
          value={date}
          onChange={(value) => setDate(value ?? '')}
          error={missing && !date ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
        />
        <RecordFailure error={action.failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.cancel')}
          </Button>
          <Button loading={action.busy} onClick={() => void action.go()}>
            {k('submit')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

/* ---------- Gönderim ---------- */

/** "Submit for Master review": eksik yoksa kaydın içerik hash'iyle gönderilir. */
export function SubmitDialog({
  record,
  source,
  onClose,
  onSaved,
}: {
  record: DrillRecordDetail | null;
  source: DrillRecordSource;
  onClose: () => void;
  onSaved: (record: DrillRecord) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillRecord.submit.${key}`, values);
  const action = useAction(
    async () =>
      record ? source.submit?.({ id: record.id, contentHash: record.contentHash }) : undefined,
    onSaved,
  );
  if (!record) return null;
  return (
    <Modal
      opened
      onClose={onClose}
      title={k('title')}
      closeButtonProps={{ 'aria-label': t('drills.close') }}
    >
      <Stack gap="sm">
        <p className={classes.hint}>
          {k('body', { no: record.recordNo, date: formatDay(record.general.drillDate) })}
        </p>
        <RecordFailure error={action.failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.cancel')}
          </Button>
          <Button loading={action.busy} onClick={() => void action.go()}>
            {k('confirm')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

/* ---------- Kaptan kararı ---------- */

/** Kaptanın (ya da vekilinin; ADR 0127) onayı ya da gerekçeli iadesi; karar gönderimin hash'iyle. */
export function RecordDecisionDialog({
  record,
  source,
  onClose,
  onSaved,
}: {
  record: DrillRecordDetail | null;
  source: DrillRecordSource;
  onClose: () => void;
  onSaved: (record: DrillRecord) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillRecord.decision.${key}`);
  const reauth = useReauth('drill_decision');
  const [reason, setReason] = useState('');
  const [missingReason, setMissingReason] = useState(false);
  const [saving, setSaving] = useState<'approved' | 'returned' | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const resetPassword = reauth.reset;
  // biome-ignore lint/correctness/useExhaustiveDependencies: yeni kayıtla açılınca sıfırlanır
  useEffect(() => {
    setReason('');
    setMissingReason(false);
    setFailure(null);
    resetPassword();
  }, [record?.id, resetPassword]);
  if (!record?.pending) return null;
  const pending = record.pending;
  const decide = async (decision: 'approved' | 'returned') => {
    if (decision === 'returned' && reason.trim() === '') {
      setMissingReason(true);
      return;
    }
    if (reauth.missing) return;
    setSaving(decision);
    setFailure(null);
    try {
      const saved = await source.decide?.({
        id: record.id,
        decision: {
          id: pending.eventId,
          decision,
          reason: reason.trim() === '' ? null : reason.trim(),
          contentHash: pending.contentHash,
        },
        ...reauth.body(),
      });
      if (saved) onSaved(saved);
    } catch (error) {
      reauth.noteError(error);
      setFailure(error);
    } finally {
      setSaving(null);
    }
  };
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={k('title')}
      closeButtonProps={{ 'aria-label': t('drills.close') }}
    >
      <Stack gap="sm">
        <p className={classes.hint}>
          {`${record.recordNo} · ${record.templateCode} · ${record.templateName} · ${formatDay(
            record.general.drillDate,
          )}`}
        </p>
        {record.capacity === 'deputy' ? (
          <Alert color="blue">{t('drillPlan.onBehalfNote')}</Alert>
        ) : null}
        <Textarea
          label={k('comment')}
          description={k('commentHint')}
          autosize
          minRows={2}
          value={reason}
          onChange={(event) => {
            setReason(event.currentTarget.value);
            setMissingReason(false);
          }}
          error={missingReason ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
        />
        <ReauthField reauth={reauth} label={t('drillPlan.decision.password')} />
        <RecordFailure error={failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.cancel')}
          </Button>
          {record.can.return ? (
            <Button
              color="red"
              variant="light"
              loading={saving === 'returned'}
              disabled={reauth.missing}
              onClick={() => void decide('returned')}
            >
              {k('return')}
            </Button>
          ) : null}
          {record.can.approve ? (
            <Button
              loading={saving === 'approved'}
              disabled={reauth.missing}
              onClick={() => void decide('approved')}
            >
              {k('approve')}
            </Button>
          ) : null}
        </div>
      </Stack>
    </Modal>
  );
}

/* ---------- Gerekçeli işlemler: yeniden açma, iptal ---------- */

export function ReasonDialog({
  record,
  kind,
  source,
  onClose,
  onSaved,
}: {
  record: DrillRecordDetail | null;
  kind: 'reopen' | 'cancel';
  source: DrillRecordSource;
  onClose: () => void;
  onSaved: (record: DrillRecord) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillRecord.${kind}.${key}`);
  const reauth = useReauth('drill_decision');
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);
  const resetPassword = reauth.reset;
  // biome-ignore lint/correctness/useExhaustiveDependencies: yeni kayıtla açılınca sıfırlanır
  useEffect(() => {
    setReason('');
    setMissing(false);
    resetPassword();
  }, [record?.id, kind, resetPassword]);
  const action = useAction(async () => {
    if (!record) return undefined;
    if (reason.trim() === '') {
      setMissing(true);
      return undefined;
    }
    try {
      return kind === 'reopen'
        ? await source.reopen?.({ id: record.id, reason: reason.trim(), ...reauth.body() })
        : await source.cancel?.({
            id: record.id,
            rowVersion: record.rowVersion,
            reason: reason.trim(),
          });
    } catch (error) {
      reauth.noteError(error);
      throw error;
    }
  }, onSaved);
  if (!record) return null;
  return (
    <Modal
      opened
      onClose={onClose}
      title={k('title')}
      closeButtonProps={{ 'aria-label': t('drills.close') }}
    >
      <Stack gap="sm">
        <p className={classes.hint}>{k('hint')}</p>
        <Textarea
          label={k('reason')}
          required
          autosize
          minRows={2}
          value={reason}
          onChange={(event) => {
            setReason(event.currentTarget.value);
            setMissing(false);
          }}
          error={missing ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
        />
        {kind === 'reopen' ? (
          <ReauthField reauth={reauth} label={t('drillPlan.decision.password')} />
        ) : null}
        <RecordFailure error={action.failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.cancel')}
          </Button>
          <Button
            {...(kind === 'cancel' ? { color: 'red' } : {})}
            loading={action.busy}
            disabled={kind === 'reopen' && reauth.missing}
            onClick={() => void action.go()}
          >
            {k('confirm')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

/* ---------- Gerçek acil durum ---------- */

/**
 * "Stopped: real emergency": gerekçe, durdurma saati ve (varsa) HSEQ kaza ya da ramak kala raporu
 * (ADR 0097). Rapor sonradan da bağlanabilir.
 */
export function StopDialog({
  record,
  source,
  onClose,
  onSaved,
}: {
  record: DrillRecordDetail | null;
  source: DrillRecordSource;
  onClose: () => void;
  onSaved: (record: DrillRecord) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillRecord.stop.${key}`);
  const [reason, setReason] = useState('');
  const [time, setTime] = useState('');
  const [incident, setIncident] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: yeni kayıtla açılınca sıfırlanır
  useEffect(() => {
    setReason('');
    setTime('');
    setIncident(null);
    setMissing(false);
  }, [record?.id]);
  const action = useAction(async () => {
    if (!record) return undefined;
    if (reason.trim() === '') {
      setMissing(true);
      return undefined;
    }
    return source.stop?.({
      id: record.id,
      rowVersion: record.rowVersion,
      stop: {
        reason: reason.trim(),
        stoppedTime: time.trim() === '' ? null : normalizeTime(time),
        hseReportId: incident,
      },
    });
  }, onSaved);
  if (!record) return null;
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={k('title')}
      closeButtonProps={{ 'aria-label': t('drills.close') }}
    >
      <Stack gap="sm">
        <Alert color="red">{k('hint')}</Alert>
        <Textarea
          label={k('reason')}
          required
          autosize
          minRows={2}
          value={reason}
          onChange={(event) => {
            setReason(event.currentTarget.value);
            setMissing(false);
          }}
          error={missing ? t('drillPlan.fieldErrors.REQUIRED') : undefined}
        />
        <TimeField
          label={k('time')}
          value={time}
          onChange={setTime}
          tzOffsetMin={record.tzOffsetMin}
        />
        <IncidentSelect record={record} value={incident} onChange={setIncident} />
        <RecordFailure error={action.failure} />
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('drillPlan.actions.cancel')}
          </Button>
          <Button color="red" loading={action.busy} onClick={() => void action.go()}>
            {k('confirm')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

export function IncidentSelect({
  record,
  value,
  onChange,
  readOnly,
}: {
  record: DrillRecordDetail;
  value: string | null;
  onChange: (value: string | null) => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Select
      label={t('drillRecord.stop.incident')}
      description={t('drillRecord.stop.incidentHint')}
      clearable
      searchable
      readOnly={readOnly}
      nothingFoundMessage={t('drillRecord.stop.noIncidents')}
      data={record.incidents.map((i) => ({
        value: i.id,
        label: `${i.number} · ${t(`drillRecord.incidentType.${i.type ?? 'near_miss'}`)} · ${i.title}`,
      }))}
      value={value}
      onChange={onChange}
    />
  );
}
